// TrainingVisitsPlanner.js
import React, { useEffect, useRef, useState } from 'react';
import API from './api';

/** ---------- Time utils ---------- */
function normalizeTime(val) {
  if (!val) return '';
  let t = String(val);
  if (t.includes('AM') || t.includes('PM')) {
    const [hm, part] = t.split(' ');
    let [h, m] = hm.split(':').map(Number);
    if (part === 'PM' && h < 12) h += 12;
    if (part === 'AM' && h === 12) h = 0;
    return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
  }
  return t.slice(0, 5);
}
function toMinutes(t) {
  if (!t) return 0;
  const [h, m] = normalizeTime(t).split(':').map(Number);
  return h * 60 + m;
}
function minutesToHHMM(mins) {
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return `${h.toString().padStart(2,'0')}:${m.toString().padStart(2,'0')}`;
}

/** ---------- UI helpers ---------- */
const COLOR_PALETTE = [
  'bg-emerald-400', 'bg-sky-400', 'bg-fuchsia-400',
  'bg-amber-400', 'bg-lime-400', 'bg-rose-400',
  'bg-cyan-400', 'bg-violet-400', 'bg-orange-400'
];
function colorForId(id) {
  if (!id && id !== 0) return 'bg-gray-400';
  const idx = Math.abs(parseInt(id, 10)) % COLOR_PALETTE.length;
  return COLOR_PALETTE[idx];
}
function initials(name='') {
  const parts = name.trim().split(/\s+/);
  return parts.slice(0,2).map(p=>p[0]?.toUpperCase()||'').join('');
}

function StatusBadge({ status }) {
  const map = {
    draft: 'bg-blue-100 text-blue-700',
    complete: 'bg-green-200 text-green-900',
    canceled: 'bg-purple-200 text-purple-700',
  };
  const cls = map[status] || 'bg-gray-200 text-gray-700';
  return <span className={`px-2 py-0.5 rounded text-xs ${cls}`}>{status || '—'}</span>;
}

function TrainingFlagBadge({ flag, isThisTrainee, lockedByOther }) {
  if (lockedByOther) {
    return (
      <span className="px-2 py-0.5 rounded text-[10px] bg-red-50 text-red-700 border border-red-200">
        Other trainee
      </span>
    );
  }
  if (!flag || flag === 'none') {
    return (
      <span className="px-2 py-0.5 rounded text-[10px] bg-gray-50 text-gray-500 border border-dashed border-gray-300">
        No training
      </span>
    );
  }
  const text = flag === 'shadow' ? 'Shadow' : 'Supervised';
  return (
    <span className={`px-2 py-0.5 rounded text-[10px] border ${isThisTrainee ? 'bg-emerald-50 text-emerald-700 border-emerald-300' : 'bg-gray-50 text-gray-500 border-gray-200'}`}>
      {text}
    </span>
  );
}

function Toast({ text, onClose }) {
  if (!text) return null;
  return (
    <div className="fixed top-4 right-4 z-[9999] text-white px-3 py-2 rounded shadow bg-slate-700">
      <div className="flex items-center gap-3">
        <span className="text-sm">{text}</span>
        <button onClick={onClose} className="text-white/90 hover:text-white text-lg leading-none">×</button>
      </div>
    </div>
  );
}

/** ---------- Main component ---------- */
export default function TrainingVisitsPlanner() {
  // trainees (teachers with training_stage != 'none')
  const [trainees, setTrainees] = useState([]);
  const [selectedTraineeId, setSelectedTraineeId] = useState('');
  const [selectedTrainee, setSelectedTrainee] = useState(null);
  const [mentor, setMentor] = useState(null);

  // weeks
  const [weekOptions, setWeekOptions] = useState([]);
  const [selectedWeekStart, setSelectedWeekStart] = useState('');
  const [weekDates, setWeekDates] = useState([]);

  // server time (for “past day”)
  const [serverDateStr, setServerDateStr] = useState('');

  // workday (timeline scale)
  const [workday, setWorkday] = useState({ start: '08:00', end: '18:00' });

  // visits per day (mentor's visits)
  const [visitsByDate, setVisitsByDate] = useState({});

  // local changes: { [visitId]: 'shadow' | 'supervised' | 'none' }
  const [changes, setChanges] = useState({});

  // visits where trainee is busy (BE check)
  // { [visitId]: "Trainee is not available..." }
  const [busyVisits, setBusyVisits] = useState({});

  const [status, setStatus] = useState('');
  const trackRefs = useRef({});

  // derive time span
  const DAY_START = workday.start || '08:00';
  const DAY_END   = workday.end   || '18:00';
  const dayStartM = toMinutes(DAY_START);
  const dayEndM   = toMinutes(DAY_END);
  const spanM     = Math.max(1, dayEndM - dayStartM);

  const pctBox = (v) => {
    const from = toMinutes(v.from);
    const to   = toMinutes(v.to);
    const left = ((from - dayStartM) / spanM) * 100;
    const width = ((to - from) / spanM) * 100;
    return {
      left: `${Math.max(0, left)}%`,
      width: `${Math.max(0, Math.min(100 - left, width))}%`
    };
  };

  function isPastDate(date) {
    if (!serverDateStr) return false;
    return date < serverDateStr;
  }

  /** ---------- initial loads ---------- */
  useEffect(() => {
    API.get('/server-time')
      .then(res => {
        const s = res.data?.serverTime || '';
        setServerDateStr(s.slice(0,10));
      })
      .catch(()=>{});

    API.get('/workday-settings')
      .then(res => setWorkday({
        start: (res.data?.start || '08:00').slice(0,5),
        end:   (res.data?.end   || '18:00').slice(0,5),
      }))
      .catch(()=>{});

    API.get('/weeks')
      .then(res => setWeekOptions(res.data || []))
      .catch(()=>{});

    API.get('/training/trainees')
      .then(res => setTrainees(res.data || []))
      .catch(()=>{});
  }, []);

  useEffect(() => {
    if (!selectedWeekStart) return;
    API.get(`/week-dates/${selectedWeekStart}`)
      .then(res => setWeekDates(res.data || []))
      .catch(()=>{});
  }, [selectedWeekStart]);

  // Toast auto-clear
  useEffect(() => {
    if (!status) return;
    const t = setTimeout(() => setStatus(''), 3000);
    return () => clearTimeout(t);
  }, [status]);

  // when trainee or week changes -> load mentor visits
  useEffect(() => {
    if (!selectedTraineeId || !selectedWeekStart) {
      setVisitsByDate({});
      setMentor(null);
      setSelectedTrainee(
        trainees.find(t => String(t.id) === String(selectedTraineeId)) || null
      );
      return;
    }

    (async () => {
      try {
        const res = await API.get('/training/mentor-week', {
          params: {
            trainee_id: selectedTraineeId,
            week_start: selectedWeekStart,
          }
        });
        setMentor(res.data?.mentor || null);
        setSelectedTrainee(res.data?.trainee || null);
        setVisitsByDate(res.data?.visits_by_date || {});
        setChanges({});
        setBusyVisits({});
      } catch (e) {
        console.error(e);
        setStatus(e.response?.data?.error || 'Failed to load mentor visits.');
        setVisitsByDate({});
        setMentor(null);
        setBusyVisits({});
      }
    })();
  }, [selectedTraineeId, selectedWeekStart, trainees]);

  /** ---------- helpers ---------- */
  function getEffectiveFlag(v) {
    const local = changes[v.id];
    if (local !== undefined) return local;
    return v.training_flag || 'none';
  }

  function isThisTrainee(v) {
    const effFlag = getEffectiveFlag(v);
    if (!selectedTraineeId) return false;
    if (!effFlag || effFlag === 'none') return false;
    const traineeId = Number(selectedTraineeId);
    const local = changes[v.id];
    if (local && local !== 'none') return v.trainee_teacher_id == null || v.trainee_teacher_id === traineeId;
    return v.trainee_teacher_id === traineeId;
  }

  function toggleVisitTraining(v) {
    if (!selectedTrainee || !selectedTrainee.training_stage) {
      setStatus('Select a trainee with a defined training stage.');
      return;
    }
    if (v.locked_by_other) {
      setStatus('This visit is already assigned to another trainee.');
      return;
    }
    if (v.status === 'complete' || v.status === 'canceled') {
      setStatus('Cannot modify training on completed or canceled visits.');
      return;
    }

    const stage = selectedTrainee.training_stage; // 'shadow' or 'supervised'
    setChanges(prev => {
      const current = prev[v.id] !== undefined ? prev[v.id] : (v.training_flag || 'none');
      const next = (current && current !== 'none') ? 'none' : stage;
      return {
        ...prev,
        [v.id]: next,
      };
    });

    // if there was a busy warning for this visit, clear it when toggling again
    setBusyVisits(prev => {
      const copy = { ...prev };
      delete copy[v.id];
      return copy;
    });
  }

  async function handleSave() {
    if (!selectedTraineeId) {
      setStatus('Select a trainee first.');
      return;
    }
    const keys = Object.keys(changes);
    if (!keys.length) {
      setStatus('Nothing to save.');
      return;
    }

    const payload = keys.map(k => ({
      visit_id: Number(k),
      training_flag: changes[k],
    }));

    try {
      const res = await API.post('/training/assign-bulk', {
        trainee_id: Number(selectedTraineeId),
        changes: payload,
      });

      const data = res.data || {};
      if (!data.success) {
        setStatus(data.error || 'Save failed.');
        return;
      }

      // map busy_details -> { visitId: reason }
      const busyMap = {};
      (data.busy_details || []).forEach(d => {
        if (!d || !d.visit_id) return;
        const base = (d.conflicts && d.conflicts.length)
          ? `Trainee is not available: ${d.conflicts.join('; ')}`
          : 'Trainee is not available in this time slot.';
        busyMap[d.visit_id] = base;
      });
      setBusyVisits(busyMap);

      let msg = `Saved training for ${data.updated || 0} visits.`;
      if (data.skipped_busy) {
        msg += ` Trainee was busy for ${data.skipped_busy} visits.`;
      }
      if (data.skipped_locked) {
        msg += ` Skipped ${data.skipped_locked} visits already locked by another trainee.`;
      }
      setStatus(msg);

      // reload data after save so FE is in sync with DB
      if (selectedTraineeId && selectedWeekStart) {
        const r2 = await API.get('/training/mentor-week', {
          params: { trainee_id: selectedTraineeId, week_start: selectedWeekStart }
        });
        setMentor(r2.data?.mentor || null);
        setSelectedTrainee(r2.data?.trainee || null);
        setVisitsByDate(r2.data?.visits_by_date || {});
        setChanges({});
        // busyMap stays, so user sees which visits were rejected
      }
    } catch (e) {
      console.error(e);
      setStatus(e.response?.data?.error || 'Save failed.');
    }
  }

  const traineeOptions = trainees || [];

  return (
    <div className="p-2 max-w-7xl mx-auto">
      <Toast text={status} onClose={() => setStatus('')} />

      <h2 className="text-lg font-bold mb-4">
        Training Visits Planner (Shadow / Supervised)
      </h2>

      <p className="text-sm text-gray-700 mb-3">
        Here the organization admin centrally manages when a trainee teacher will attend the mentor&apos;s visits.
        Each block on the timeline represents one confirmed visit of the mentor. By clicking on a visit you toggle
        whether the trainee is attached to that visit (as <b>shadow</b> or <b>supervised</b>, based on the
        trainee&apos;s current training stage) or there is <b>no training</b> on that visit.
        Before saving, the system automatically checks if the trainee is available in that time slot
        (no overlapping visits and within the teacher&apos;s availability schedule). After a successful save,
        both the trainee and the mentor receive an email and web push notification with a summary of the new
        training visits.
      </p>

      <div className="flex flex-col lg:flex-row gap-4 items-center lg:items-end mb-4">
        <div>
          <label className="block mb-1">Trainee teacher:</label>
          <select
            className="border px-2 py-1 w-72"
            value={selectedTraineeId}
            onChange={e => setSelectedTraineeId(e.target.value)}
          >
            <option value="">-- Select trainee --</option>
            {traineeOptions.map(t => (
              <option key={t.id} value={t.id}>
                {t.name} {t.training_stage ? `(${t.training_stage})` : ''}{t.mentor_name ? ` – mentor: ${t.mentor_name}` : ''}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block mb-1">Week:</label>
          <select
            className="border px-2 py-1 w-56"
            value={selectedWeekStart}
            onChange={e => setSelectedWeekStart(e.target.value)}
          >
            <option value="">-- Choose week --</option>
            {weekOptions.map((w,i)=>
              <option key={i} value={w.value}>{w.label}</option>
            )}
          </select>
        </div>

        {selectedTrainee && (
          <div className="text-sm bg-gray-50 border rounded px-3 py-2">
            <div>
              <span className="font-semibold">Trainee:</span>{' '}
              {selectedTrainee.name} ({selectedTrainee.training_stage || 'none'})
            </div>
            {mentor && (
              <div>
                <span className="font-semibold">Mentor:</span>{' '}
                {mentor.name}
              </div>
            )}
          </div>
        )}
      </div>

      {selectedTraineeId && selectedWeekStart && (
        <>
          <div className="flex flex-wrap gap-3 text-xs text-gray-600 mb-3">
            <span className="flex items-center gap-2">
              <span className="inline-block w-4 h-3 rounded bg-emerald-300" /> Training assigned to this trainee
            </span>
            <span className="flex items-center gap-2">
              <span className="inline-block w-4 h-3 rounded bg-gray-300" /> No training
            </span>
            <span className="flex items-center gap-2">
              <span className="inline-block w-4 h-3 rounded bg-red-300" /> Locked / other trainee
            </span>
            <span>
              Click on a visit box = toggle (none ↔ {selectedTrainee?.training_stage || 'shadow/supervised'}) for this trainee, if it is not locked or in the past.
            </span>
          </div>

          <div className="space-y-4">
            {weekDates.map(({date, dayName}) => {
              const list = (visitsByDate[date] || []);
              const isPastDay = isPastDate(date);
              return (
                <div key={date} className="border rounded p-3 shadow-sm">
                  <div className={`font-semibold mb-2 px-2 py-1 rounded flex items-center gap-2 ${
                    isPastDay
                      ? 'text-gray-400 bg-gray-50'
                      : 'text-gray-800 bg-gray-100'
                  }`}>
                    <span>{dayName} – {date}</span>
                    {!!list.length && (
                      <span className="text-xs px-1.5 py-0.5 rounded border bg-emerald-50 text-emerald-700 border-emerald-200">
                        Visits {list.length}
                      </span>
                    )}
                  </div>

                  <div
                    className="relative h-16 rounded bg-gray-100 border select-none overflow-visible"
                    ref={el => (trackRefs.current[date] = el)}
                  >
                    {/* hourly ticks */}
                    {Array.from({length: (spanM/60)+1}).map((_,i)=>{
                      const leftPct = (i*60)/spanM*100;
                      return (
                        <div key={i}>
                          <div
                            className="absolute top-4 bottom-0 border-l border-gray-300"
                            style={{ left: `${leftPct}%` }}
                          />
                          <div
                            className="absolute top-0 text-[10px] text-gray-500 -translate-x-1/2"
                            style={{ left: `${leftPct}%` }}
                          >
                            {minutesToHHMM(dayStartM + i*60).slice(0,2)}
                          </div>
                        </div>
                      );
                    })}

                    {/* visits */}
                    {list.map((v, idx) => {
                      const box = pctBox(v);
                      const effFlag = getEffectiveFlag(v);
                      const thisTrainee = isThisTrainee(v);
                      const locked = v.locked_by_other;

                      let borderCls = 'border-gray-400';
                      let fillCls = 'bg-gray-300 opacity-60';
                      if (locked) {
                        borderCls = 'border-red-500';
                        fillCls = 'bg-red-300 opacity-60';
                      } else if (thisTrainee && effFlag !== 'none') {
                        borderCls = 'border-emerald-700';
                        fillCls = `${colorForId(v.student_id)} opacity-60`;
                      }

                      const clickable = !isPastDay && !locked && v.status !== 'complete' && v.status !== 'canceled';

                      const baseTitle = `${v.student_name || '—'} ${v.from}–${v.to}`;
                      const finalTitle = busyVisits[v.id]
                        ? `${baseTitle} · ${busyVisits[v.id]}`
                        : baseTitle;

                      return (
                        <div
                          key={v.id || idx}
                          className={`absolute top-5 bottom-2 rounded shadow-sm overflow-hidden bg-white border ${borderCls} ${clickable ? 'cursor-pointer' : 'cursor-default'}`}
                          style={{ left: box.left, width: box.width }}
                          title={finalTitle}
                          onClick={() => clickable && toggleVisitTraining(v)}
                        >
                          <div className={`h-full ${fillCls}`} />
                          <div className="absolute inset-0 flex items-center justify-between px-2 text-xs gap-1">
                            <span className="font-mono">
                              {v.from}–{v.to}
                            </span>
                            <span className="hidden sm:inline font-semibold">
                              {v.student_name || '—'}
                            </span>
                            <span className="sm:hidden font-semibold">
                              {initials(v.student_name || '')}
                            </span>
                            <TrainingFlagBadge
                              flag={effFlag}
                              isThisTrainee={thisTrainee}
                              lockedByOther={locked}
                            />
                            <StatusBadge status={v.status} />
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {!!list.length && (
                    <div className="mt-2 space-y-1">
                      {list.map((v, idx) => {
                        const effFlag = getEffectiveFlag(v);
                        const thisTrainee = isThisTrainee(v);
                        const locked = v.locked_by_other;
                        const busyMsg = busyVisits[v.id];
                        return (
                          <div key={'row'+idx} className="flex items-center justify-between text-sm">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className={`inline-block w-3 h-3 rounded-full ${
                                locked
                                  ? 'bg-red-400'
                                  : thisTrainee && effFlag !== 'none'
                                    ? colorForId(v.student_id)
                                    : 'bg-gray-300'
                              }`} />
                              <span className={locked ? 'font-semibold text-red-700' : 'font-semibold'}>
                                {v.student_name || '—'}
                              </span>
                              <span className="font-mono">
                                {v.from}–{v.to}
                              </span>
                              <TrainingFlagBadge
                                flag={effFlag}
                                isThisTrainee={thisTrainee}
                                lockedByOther={locked}
                              />
                              <StatusBadge status={v.status} />
                              {busyMsg && (
                                <span
                                  className="text-xs text-red-600 ml-2"
                                  title={busyMsg}
                                >
                                  {busyMsg}
                                </span>
                              )}
                            </div>
                            <button
                              className={
                                'px-2 py-0.5 rounded border text-xs ' +
                                (locked || isPastDay || v.status === 'complete' || v.status === 'canceled'
                                  ? 'bg-gray-50 text-gray-400 cursor-not-allowed border-gray-200'
                                  : 'bg-white text-blue-600 border-blue-300 hover:bg-blue-50')
                              }
                              disabled={locked || isPastDay || v.status === 'complete' || v.status === 'canceled'}
                              onClick={() => toggleVisitTraining(v)}
                              title={busyMsg || undefined}
                            >
                              {locked ? 'Locked' : (getEffectiveFlag(v) !== 'none' ? 'Remove training' : 'Add training')}
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div className="mt-6">
            <button
              onClick={handleSave}
              className="px-4 py-2 rounded text-white bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400"
              disabled={!Object.keys(changes).length}
            >
              Save training assignments
            </button>
            {!!Object.keys(changes).length && (
              <span className="ml-3 text-xs text-gray-600">
                Pending changes: {Object.keys(changes).length}
              </span>
            )}
          </div>
        </>
      )}
    </div>
  );
}



